(() => {
  if (customElements.get('hh-variant-comparison')) return;
  let cartQueue = Promise.resolve();
  const normal = value => String(value).normalize('NFC').toLocaleLowerCase().trim();
  class VariantComparison extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.data = JSON.parse(this.querySelector('[data-comparison-data]').textContent);
      this.labels = this.data.labels;
      this.rows = [...this.querySelectorAll('[data-row]')];
      this.expanded = false;
      this.search = this.querySelector('[data-search]');
      this.more = this.querySelector('[data-show-more]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'da', {style:'currency',currency:this.dataset.currency});
      this.prepareSpecifications();
      for (const row of this.rows) {
        row.search = normal(row.textContent + ' ' + row.dataset.variantTitle + ' ' + (this.commonSearch || ''));
        this.updateQuantity(row);
      }
      this.querySelector('[data-search-wrapper]')?.removeAttribute('hidden');
      this.updateSelection(new URL(location.href).searchParams.get('variant'));
      this.updateVisible();
      this.addEventListener('input', event => {
        if (event.target === this.search) this.updateVisible();
        else if (event.target.name === 'quantity') this.updateQuantity(event.target.closest('[data-row]'));
      }, {signal});
      this.addEventListener('click', event => {
        const collapse = event.target.closest('[data-collapse]');
        if (collapse) {
          const expanded = collapse.getAttribute('aria-expanded') !== 'true';
          collapse.setAttribute('aria-expanded', String(expanded));
          collapse.setAttribute('aria-label', expanded ? collapse.dataset.collapseLabel : collapse.dataset.expandLabel);
          this.querySelector('[data-content]').hidden = !expanded;
        }
        const notify = event.target.closest('[data-notify]');
        if (notify) this.openNotification(notify);
        const step = event.target.closest('[data-quantity-step]');
        if (step) {
          const row = step.closest('[data-row]'), input = row.querySelector('[name="quantity"]');
          if (!input.checkValidity()) input.value = input.min;
          Number(step.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown();
          this.updateQuantity(row);
        }
        if (event.target.closest('[data-show-more]')) {
          this.expanded = !this.expanded;
          this.updateVisible();
        }
      }, {signal});
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-purchase]');
        if (!form) return;
        event.preventDefault();
        if (form.closest('[data-unavailable]') || !form.reportValidity() || form.dataset.busy) return;
        form.dataset.busy = 'true';
        form.setAttribute('aria-busy','true');
        form.querySelector('[type="submit"]').disabled = true;
        const quantity = Number(form.elements.quantity.value);
        cartQueue = cartQueue.catch(() => {}).then(() => this.addToCart(form, quantity));
      }, {signal});
      const selector = document.querySelector('.product-template select[data-variant-select]');
      selector?.addEventListener('change', () => this.updateSelection(selector.value), {signal});
      window.addEventListener('popstate', () => this.updateSelection(new URL(location.href).searchParams.get('variant')), {signal});
    }

    disconnectedCallback() { this.controller?.abort(); }

    prepareSpecifications() {
      try {
        const model = window.HhSpecificationModel.build([this.data]);
        const variantFacts = new Map(model.variants.map(v => [String(v[0]),new Map(v[4])]));
        if (!model.fields.length) {
          this.closest('.hh-variants-container').hidden = true;
          return;
        }
        const groupOrder = ['Dimensioner', 'Tekniske egenskaber', 'Materiale og farve', 'Antal og pakning', 'Øvrigt'];
        const order = model.fields.map((field,index) => index).sort((a,b) => {
          const rank = field => { const index = groupOrder.indexOf(field[4]); return index < 0 ? groupOrder.length : index; };
          return rank(model.fields[a]) - rank(model.fields[b]);
        });
        const format = (field,value) => {
          const number = typeof value === 'number' ? new Intl.NumberFormat(document.documentElement.lang || 'da',{maximumFractionDigits:12}).format(value) : value;
          return field[2] === 'number' && field[3] ? `${number} ${field[3]}` : number;
        };
        const shared = this.dataset.complete === 'true' ? order.filter(index => {
          const values = [...variantFacts.values()];
          return values.length > 1 && values.every(facts => facts.has(index) && facts.get(index) === values[0].get(index));
        }) : [];
        const varying = order.filter(index => !shared.includes(index));
        const rowFields = varying.length ? varying : order;
        const commonFields = varying.length ? shared : [];
        const common = this.querySelector('[data-common]'), commonSpecs = this.querySelector('[data-common-specs]');
        commonSpecs.replaceChildren();
        const firstFacts = variantFacts.values().next().value;
        for (const index of commonFields) {
          const item = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd');
          label.textContent = model.fields[index][1];
          value.textContent = format(model.fields[index],firstFacts.get(index));
          item.append(label,value); commonSpecs.append(item);
        }
        common.hidden = !commonFields.length;
        this.commonSearch = commonFields.length ? commonSpecs.textContent : '';
        this.style.setProperty('--hh-spec-columns', Math.min(4, rowFields.length));
        for (const row of this.rows) {
          const facts = variantFacts.get(row.dataset.variantId) || new Map();
          const specs = row.querySelector('[data-specs]');
          const fragment = document.createDocumentFragment();
          for (const index of rowFields) {
            const field = model.fields[index];
            const item = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd');
            label.textContent = field[1];
            value.textContent = facts.has(index) ? format(field,facts.get(index)) : '—';
            item.append(label,value); fragment.append(item);
          }
          specs.replaceChildren(fragment);
        }
      } catch { }
    }

    updateVisible() {
      const terms = normal(this.search?.value || '').split(/\s+/).filter(Boolean);
      let matches = 0;
      for (const row of this.rows) {
        const match = terms.every(term => row.search.includes(term));
        row.hidden = !match || (!terms.length && !this.expanded && matches >= 8);
        if (match) matches++;
      }
      this.querySelector('[data-empty]').hidden = matches > 0;
      this.more.hidden = !!terms.length || this.rows.length <= 8;
      this.more.textContent = this.expanded ? this.labels.less : this.labels.more.replace('__COUNT__',String(this.rows.length));
    }

    updateQuantity(row) {
      const input = row.querySelector('[name="quantity"]'), quantity = Number(input.value);
      const valid = input.checkValidity() && Number.isFinite(quantity);
      row.querySelector('[data-quantity-step="-1"]').disabled = input.disabled || !valid || quantity <= Number(input.min);
      row.querySelector('[data-quantity-step="1"]').disabled = input.disabled || (input.max !== '' && quantity >= Number(input.max));
      const total = row.querySelector('[data-total]');
      total.hidden = !valid || (quantity <= 1 && row.dataset.saleUnit !== 'meter');
      if (valid) total.textContent = row.dataset.saleUnit === 'meter' ? `${quantity.toLocaleString('da')} meter = ${this.money.format(Number(row.dataset.price)*quantity/100)}` : this.labels.total.replace('__PRICE__',this.money.format(Number(row.dataset.price) * quantity / 100));
      row.querySelector('[data-delivery-single]')?.toggleAttribute('hidden', quantity > 1);
      row.querySelector('[data-delivery-bulk]')?.toggleAttribute('hidden', quantity <= 1);
    }

    updateSelection(id) {
      const selected = id || document.querySelector('.product-template select[data-variant-select]')?.value;
      for (const row of this.rows) {
        row.toggleAttribute('data-selected', row.dataset.variantId === selected);
      }
    }

    async openNotification(button) {
      if (this.notifying) return;
      const row = button.closest('[data-row]');
      const option = document.querySelector(`.product-template [data-select-options] [data-variant-id="${row.dataset.variantId}"]:not(.disabled)`);
      if (!option) {
        location.assign(row.dataset.variantUrl);
        return;
      }
      this.notifying = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const message = row.querySelector('[data-message]');
      message.textContent = this.labels.notify_loading;
      message.removeAttribute('data-error');
      option.click();
      try {
        const nativeButton = await new Promise((resolve, reject) => {
          const check = () => {
            if (!this.isConnected || new URL(location.href).searchParams.get('variant') !== row.dataset.variantId) {
              finish(); reject(new Error('Selection changed')); return;
            }
            const trigger = document.querySelector('.product-template #klaviyo-bis-button-container button');
            if (trigger && !trigger.disabled && trigger.getClientRects().length) {
              finish(); resolve(trigger);
            }
          };
          const observer = new MutationObserver(check);
          const timer = setTimeout(() => { finish(); reject(new Error('Notification unavailable')); }, 8000);
          const finish = () => { clearTimeout(timer); observer.disconnect(); };
          observer.observe(document.querySelector('.product-template'), {childList:true,subtree:true,attributes:true});
          check();
        });
        nativeButton.click();
        message.textContent = '';
      } catch {
        message.textContent = this.labels.notify_error;
        message.dataset.error = 'true';
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.notifying = false;
      }
    }

    async addToCart(form, quantity) {
      const row = form.closest('[data-row]');
      const button = form.querySelector('[type="submit"]'), message = form.querySelector('[data-message]');
      message.textContent = ''; message.removeAttribute('data-error');
      const root = window.Shopify?.routes?.root || '/';
      let added = false, received = false;
      try {
        const response = await fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:row.dataset.variantId,quantity}]})});
        if (!response.ok) {
          received = response.status >= 400 && response.status < 500;
          const result = await response.json().catch(() => ({}));
          try {
            const cart = window.MinimogTheme?.Cart;
            if (cart) {
              await cart.refreshCart();
              await cart.renderNewCart();
              await window.Shopify.onCartUpdate(cart.cart, false);
            }
          } catch (_) {}
          throw new Error(typeof result.description === 'string' ? result.description : this.labels.error);
        }
        added = true;
        message.textContent = this.labels.added.replace('__COUNT__',String(quantity)).replace('__VARIANT__',row.dataset.variantTitle);
        const cart = window.MinimogTheme?.Cart;
        if (cart) {
          await cart.refreshCart();
          await cart.renderNewCart();
          window.MinimogEvents?.emit('ON_CART_UPDATE',cart.cart);
        }
      } catch (error) {
        if (added) message.textContent += ' '+this.labels.cart_refresh;
        else { message.dataset.error = 'true'; message.textContent = received ? error.message : this.labels.uncertain; }
      } finally {
        delete form.dataset.busy;
        form.removeAttribute('aria-busy');
        button.disabled = row.hasAttribute('data-unavailable');
      }
    }
  }
  customElements.define('hh-variant-comparison', VariantComparison);
})();
